import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import {
  X,
  MapPin,
  Loader2,
  Search,
  DoorOpen,
  DoorClosed,
  Bath,
  Info,
  Building2,
  ArrowRight,
} from "lucide-react";
import type { Company, Hall } from "../types";
import { useAppStore } from "../store/useAppStore";
import { companiesService } from "../services/companiesService";
import { eventsService } from "../services/eventsService";
import { maxPlatform } from "../services/maxPlatform";
import { PageHeader } from "../components/PageHeader";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { MapViewport, type MapViewportHandle } from "../components/MapViewport";
import { FloorplanOverlayMarkers } from "../components/FloorplanOverlayMarkers";
import { PinCardLabels } from "../components/PinCardLabels";
import { normRot } from "../components/pinLabelFit";
import {
  parseSvgBoardSize,
  parseBoothRectsFromSvg,
  parseLabeledBoothRectsFromSvg,
  normalizeBoothCode,
  sceneHasContent,
  sceneBoothRects,
  type FloorplanSceneObject,
  type FloorplanObjectKind,
} from "@mvk/shared";
import { formatCategoriesDisplay } from "../utils/categoryLabel";
import {
  formatBoothLabel,
  formatCompanyLocation,
  isSharedHall,
} from "../utils/locationLabel";
import {
  eventHexAlpha,
  eventHexRgb,
  eventHexRgba,
  normalizeEventHex,
} from "../utils/eventTheme";
import { boardPctToFramePx } from "../utils/mapBoardCoords";
import { toast } from "sonner";

const PIN_COLORS = [
  "#6366f1", "#ec4899", "#14b8a6", "#f59e0b",
  "#8b5cf6", "#ef4444", "#06b6d4", "#84cc16",
];
const DEFAULT_EVENT_PRIMARY = "#4f46e5";

function hexOk(v: string | undefined | null): v is string {
  return Boolean(v && /^#[0-9a-fA-F]{6}$/.test(v.trim()));
}

type BoothPinGroup = {
  key: string;
  booth: string;
  x: number;
  y: number;
  left: number;
  top: number;
  w: number;
  h: number;
  companies: Company[];
  color: string;
};

function boardFromHall(hall?: Hall | null): { width: number; height: number } {
  // Underlay-first: board matches Corel SVG page.
  if (hall?.floorplanSvg) return parseSvgBoardSize(hall.floorplanSvg);
  if (hall?.floorplanWidth && hall?.floorplanHeight) {
    return { width: hall.floorplanWidth, height: hall.floorplanHeight };
  }
  if (hall?.floorplanScene && sceneHasContent(hall.floorplanScene)) {
    return { width: hall.floorplanScene.width, height: hall.floorplanScene.height };
  }
  return { width: 1000, height: 700 };
}

function hallHasPlan(hall?: Hall | null): boolean {
  return Boolean(
    (hall?.floorplanScene && sceneHasContent(hall.floorplanScene)) || hall?.floorplanSvg,
  );
}

/** Mini-card copy for navigation / service objects on the visitor map. */
function techPlaceCard(o: FloorplanSceneObject): {
  title: string;
  subtitle: string;
  kind: FloorplanObjectKind;
  showLogo: boolean;
} {
  const label = (o.label || "").trim();
  switch (o.kind) {
    case "entrance":
      return {
        title: label || "Вход",
        subtitle: "Вход в зал мероприятия",
        kind: o.kind,
        showLogo: false,
      };
    case "exit":
      return {
        title: label || "Выход",
        subtitle: "Выход из зала",
        kind: o.kind,
        showLogo: false,
      };
    case "toilet":
      return {
        title: label || "Туалет",
        subtitle: "Уборная на схеме",
        kind: o.kind,
        showLogo: false,
      };
    case "stairs":
      return {
        title: label || "Лестница",
        subtitle: "Лестница / переход между уровнями",
        kind: o.kind,
        showLogo: false,
      };
    case "info":
      return {
        title: label || "Инфо",
        subtitle: "Стойка информации",
        kind: o.kind,
        showLogo: false,
      };
    case "service":
      return {
        title: label || "Сервис",
        subtitle: "Сервисная зона / фойе",
        kind: o.kind,
        showLogo: false,
      };
    case "zone": {
      const isOffice = /офис|organizer|mvk/i.test(label);
      return {
        title: label || "Зона",
        subtitle: isOffice ? "Офис организаторов" : "Зона на схеме",
        kind: o.kind,
        showLogo: isOffice,
      };
    }
    case "event_link":
      return {
        title: label || "Другая выставка",
        subtitle: "Переход на карту соседней выставки в этом зале",
        kind: o.kind,
        showLogo: false,
      };
    default:
      return {
        title: label || "Объект",
        subtitle: "Объект на схеме",
        kind: o.kind,
        showLogo: false,
      };
  }
}

function TechKindIcon({ kind }: { kind: FloorplanObjectKind }) {
  const cls = "text-slate-600 shrink-0";
  switch (kind) {
    case "entrance":
      return <DoorOpen size={22} className={cls} />;
    case "exit":
      return <DoorClosed size={22} className={cls} />;
    case "toilet":
      return <Bath size={22} className={cls} />;
    case "info":
      return <Info size={22} className={cls} />;
    case "zone":
    case "service":
      return <Building2 size={22} className={cls} />;
    case "event_link":
      return <ArrowRight size={22} className="text-indigo-600 shrink-0" />;
    default:
      return <MapPin size={22} className={cls} />;
  }
}

type SnapRect = {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  code?: string;
};

function matchPinsToBoothRects(
  groups: Array<Omit<BoothPinGroup, "left" | "top" | "w" | "h">>,
  rects: SnapRect[],
): BoothPinGroup[] {
  const pool = [...rects];
  return groups.map((g) => {
    const want = normalizeBoothCode(g.booth);
    // Prefer exact booth code — never steal a neighbour cell by proximity.
    if (want) {
      const byCode = pool.findIndex((r) => normalizeBoothCode(r.code) === want);
      if (byCode >= 0) {
        const r = pool.splice(byCode, 1)[0]!;
        return { ...g, left: r.x, top: r.y, w: r.w, h: r.h, x: r.cx, y: r.cy };
      }
      // Numbered stand without a matching cell: keep free pin, do not snap.
      const size = 3.2;
      return {
        ...g,
        left: Math.max(0, g.x - size / 2),
        top: Math.max(0, g.y - size / 2),
        w: size,
        h: size,
      };
    }
    let bestIdx = -1;
    let bestD = Number.POSITIVE_INFINITY;
    for (let i = 0; i < pool.length; i++) {
      const r = pool[i]!;
      const d = (r.cx - g.x) ** 2 + (r.cy - g.y) ** 2;
      if (d < bestD) {
        bestD = d;
        bestIdx = i;
      }
    }
    if (bestIdx >= 0 && bestD < 8 * 8) {
      const r = pool.splice(bestIdx, 1)[0]!;
      return { ...g, left: r.x, top: r.y, w: r.w, h: r.h, x: r.cx, y: r.cy };
    }
    const size = 3.2;
    return {
      ...g,
      left: Math.max(0, g.x - size / 2),
      top: Math.max(0, g.y - size / 2),
      w: size,
      h: size,
    };
  });
}

export function MapPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { selectedEventId, selectEvent } = useAppStore();
  const highlightId =
    (location.state as { highlightCompanyId?: string } | null)?.highlightCompanyId ?? null;
  const viewportRef = useRef<MapViewportHandle>(null);
  const highlightDoneKey = useRef<string>("");

  const [halls, setHalls] = useState<Hall[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mapPublished, setMapPublished] = useState(true);
  const [eventLogoUrl, setEventLogoUrl] = useState<string | null>(null);
  const [eventPrimary, setEventPrimary] = useState(DEFAULT_EVENT_PRIMARY);
  const [readySvgKey, setReadySvgKey] = useState("");
  const [selectedPin, setSelectedPin] = useState<string | null>(highlightId ?? null);
  const [selectedTechId, setSelectedTechId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [viewZoom, setViewZoom] = useState(1);
  /** Destination exhibition colors for event_link plaques (id → #rrggbb). */
  const [linkAccentByEventId, setLinkAccentByEventId] = useState<Record<string, string>>(
    {},
  );
  const pinTapRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!selectedEventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setReadySvgKey("");
      try {
        const [data, event] = await Promise.all([
          companiesService.listForMap(selectedEventId),
          eventsService.getById(selectedEventId).catch(() => null),
        ]);
        if (!cancelled) {
          setCompanies(data.companies);
          setHalls(data.halls);
          setMapPublished(event?.mapPublished !== false);
          setEventLogoUrl((event?.logoUrl || event?.heroImage || "").trim() || null);
          setEventPrimary(
            eventHexRgb(normalizeEventHex(event?.theme?.primaryColor, DEFAULT_EVENT_PRIMARY)),
          );
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!cancelled) setLoadError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, selectedEventId]);

  // MAX: keep sheet expanded and block accidental dismiss while panning the map.
  useEffect(() => {
    if (!maxPlatform.isAvailable()) return;
    maxPlatform.lockHostGesturesForMap();
    void maxPlatform.enterFullscreen();
    const timers = [80, 300, 800].map((ms) =>
      window.setTimeout(() => maxPlatform.lockHostGesturesForMap(), ms),
    );
    return () => {
      for (const id of timers) window.clearTimeout(id);
    };
  }, []);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedPin),
    [companies, selectedPin],
  );

  const activeHall = useMemo(
    () => halls.find((h) => hallHasPlan(h)) ?? halls[0],
    [halls],
  );

  const board = boardFromHall(activeHall);
  /**
   * Visitor map = placement only (pins + tech objects).
   * Corel SVG underlay is admin reference only — never shown in the app (avoids duplicate labels).
   */
  const labelScale =
    typeof activeHall?.floorplanScene?.labelScale === "number"
      ? Math.min(2.2, Math.max(0.7, activeHall.floorplanScene.labelScale))
      : 1.15;
  const pinLabelRotationDeg =
    activeHall?.floorplanScene?.pinLabelRotationDeg === 90 ||
    activeHall?.floorplanScene?.pinLabelRotationDeg === 180 ||
    activeHall?.floorplanScene?.pinLabelRotationDeg === 270
      ? activeHall.floorplanScene.pinLabelRotationDeg
      : 0;
  const labelsUpright = activeHall?.floorplanScene?.labelsUpright !== false;
  const boardRotation =
    activeHall?.floorplanScene?.rotationDeg === 90 ||
    activeHall?.floorplanScene?.rotationDeg === 180 ||
    activeHall?.floorplanScene?.rotationDeg === 270
      ? activeHall.floorplanScene.rotationDeg
      : 0;
  /** Navigation markers (вход, туалет, офис…) for the visitor app. */
  const archMarkers = useMemo(() => {
    const scene = activeHall?.floorplanScene;
    if (!scene?.objects?.length) return [];
    return scene.objects.filter(
      (o) => o.kind !== "booth" && o.showInApp !== false,
    );
  }, [activeHall?.floorplanScene]);

  useEffect(() => {
    const ids = [
      ...new Set(
        archMarkers
          .filter((o) => o.kind === "event_link" && o.linkEventId)
          .map((o) => o.linkEventId!),
      ),
    ];
    if (!ids.length) {
      setLinkAccentByEventId({});
      return;
    }
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      await Promise.all(
        ids.map(async (eid) => {
          const fromScene = archMarkers.find(
            (o) => o.linkEventId === eid && hexOk(o.linkAccent),
          )?.linkAccent;
          if (fromScene) {
            next[eid] = fromScene;
            return;
          }
          try {
            const evt = await eventsService.getById(eid);
            const raw = (evt?.theme?.primaryColor ?? "").trim();
            if (raw) next[eid] = eventHexRgb(normalizeEventHex(raw));
          } catch {
            /* ignore */
          }
        }),
      );
      if (!cancelled) setLinkAccentByEventId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [archMarkers]);

  const markerAccentById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const o of archMarkers) {
      if (o.kind !== "event_link") continue;
      const c =
        (hexOk(o.linkAccent) ? o.linkAccent! : null) ??
        (o.linkEventId ? linkAccentByEventId[o.linkEventId] : undefined);
      if (c) out[o.id] = c;
    }
    return out;
  }, [archMarkers, linkAccentByEventId]);

  const selectedTech = useMemo(
    () => archMarkers.find((o) => o.id === selectedTechId) ?? null,
    [archMarkers, selectedTechId],
  );
  const selectedTechCard = selectedTech ? techPlaceCard(selectedTech) : null;
  const selectedLinkAccent =
    selectedTech?.kind === "event_link"
      ? markerAccentById[selectedTech.id] ?? eventPrimary
      : eventPrimary;

  const schemeKey = `place:${activeHall?.id ?? ""}:${archMarkers.length}`;
  const floorReady = readySvgKey === schemeKey;

  useEffect(() => {
    setReadySvgKey(schemeKey);
    window.setTimeout(() => viewportRef.current?.fit(), 0);
  }, [schemeKey]);

  const hideHall = useMemo(() => isSharedHall(companies), [companies]);

  const pins = useMemo(() => {
    if (!activeHall) return companies;
    const hallPins = companies.filter(
      (c) =>
        !c.mapPoint.hallId ||
        c.mapPoint.hallId === activeHall.id ||
        c.hall === activeHall.label,
    );
    return hallPins.length > 0 ? hallPins : companies;
  }, [companies, activeHall]);

  /**
   * Hit-targets on the map.
   * - Same booth + same geometry → one shared pin (list in the sheet).
   * - Same booth but different x/y/w/h (admin “Разложить”) → separate tappable pins.
   */
  const boothPins = useMemo((): BoothPinGroup[] => {
    const byKey = new Map<string, Omit<BoothPinGroup, "left" | "top" | "w" | "h">>();
    const sized = new Map<string, { w: number; h: number }>();
    const round1 = (n: number) => Math.round(n * 10) / 10;
    pins.forEach((c, i) => {
      const x = Number.isFinite(c.mapPoint.x) ? c.mapPoint.x : 0;
      const y = Number.isFinite(c.mapPoint.y) ? c.mapPoint.y : 0;
      if (x <= 0 && y <= 0) return;
      const booth = (c.booth || "").trim().toUpperCase();
      // Visitor map: only numbered stands — orphan placements without booth
      // used to stack under real pins (shown as "—").
      if (!booth) return;
      const w = c.mapPoint.w && c.mapPoint.w > 0 ? c.mapPoint.w : 0;
      const h = c.mapPoint.h && c.mapPoint.h > 0 ? c.mapPoint.h : 0;
      // Distinct geometry → own pin, even if booth number matches (A413 collective).
      const key = `${booth}@${round1(x)},${round1(y)},${round1(w)},${round1(h)}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.companies.push(c);
        return;
      }
      const pinColor =
        c.pinColor && /^#[0-9a-fA-F]{6}$/.test(c.pinColor)
          ? c.pinColor
          : PIN_COLORS[i % PIN_COLORS.length];
      byKey.set(key, {
        key,
        booth: c.booth || "",
        x,
        y,
        companies: [c],
        color: pinColor,
      });
      if (w > 0 && h > 0) sized.set(key, { w, h });
    });
    const groups = [...byKey.values()];
    const needSnap = groups.filter((g) => !sized.has(g.key));
    const pad = activeHall?.floorplanScene?.contentPad;
    const hasContentPad = Boolean(
      pad && (pad.left || pad.top || pad.right || pad.bottom),
    );
    const fromScene = sceneBoothRects(activeHall?.floorplanScene);
    // After «Поле ↑» pins live in padded board space; Corel SVG is still
    // full-bleed — snapping to it misaligns stands vs admin.
    const fromSvgLabeled =
      !hasContentPad && fromScene.length === 0 && activeHall?.floorplanSvg
        ? parseLabeledBoothRectsFromSvg(activeHall.floorplanSvg)
        : [];
    const fromSvgPlain =
      !hasContentPad &&
      fromScene.length === 0 &&
      fromSvgLabeled.length === 0 &&
      activeHall?.floorplanSvg
        ? parseBoothRectsFromSvg(activeHall.floorplanSvg)
        : [];
    const snapPool: SnapRect[] = fromScene.length
      ? fromScene
      : fromSvgLabeled.length
        ? fromSvgLabeled
        : fromSvgPlain;
    const snapped =
      needSnap.length && snapPool.length
        ? matchPinsToBoothRects(needSnap, snapPool)
        : [];
    const snappedByKey = new Map(snapped.map((g) => [g.key, g]));
    return groups.map((g) => {
      const sz = sized.get(g.key);
      if (sz) {
        return {
          ...g,
          w: sz.w,
          h: sz.h,
          left: g.x - sz.w / 2,
          top: g.y - sz.h / 2,
        };
      }
      return snappedByKey.get(g.key) ?? {
        ...g,
        w: 3.2,
        h: 3.2,
        left: Math.max(0, g.x - 1.6),
        top: Math.max(0, g.y - 1.6),
      };
    });
  }, [pins, activeHall?.floorplanScene, activeHall?.floorplanSvg]);

  /**
   * «Показать на карте» from company card: select pin + pan/zoom to stand.
   * Previously only opened /map — highlightCompanyId was never focused.
   */
  useEffect(() => {
    if (!highlightId || loading || !floorReady || !mapPublished) return;
    const doneKey = `${location.key}:${highlightId}`;
    if (highlightDoneKey.current === doneKey) return;

    const group = boothPins.find((g) => g.companies.some((c) => c.id === highlightId));
    setSelectedTechId(null);
    setSelectedPin(highlightId);

    if (!group) {
      if (loading) return;
      highlightDoneKey.current = doneKey;
      const known = companies.some((c) => c.id === highlightId);
      if (known) {
        toast.message(
          t("map.company_not_placed", {
            defaultValue: "Компания есть в каталоге, но ещё не размещена на схеме",
          }),
        );
      }
      return;
    }

    const cx = group.left + group.w / 2;
    const cy = group.top + group.h / 2;
    const { fx, fy } = boardPctToFramePx(
      cx,
      cy,
      board.width,
      board.height,
      boardRotation,
    );

    // Do not name this `t` — it shadows useTranslation().t and throws TDZ
    // ("Cannot access 'st' before initialization") when toast uses t() above.
    const focusTimer = window.setTimeout(() => {
      const fitZ = viewportRef.current?.getFitZoom() ?? 1;
      viewportRef.current?.focusPoint(fx, fy, Math.min(fitZ * 2.2, fitZ * 3));
      highlightDoneKey.current = doneKey;
    }, 220);
    return () => window.clearTimeout(focusTimer);
  }, [
    highlightId,
    loading,
    floorReady,
    mapPublished,
    boothPins,
    board.width,
    board.height,
    boardRotation,
    companies,
    location.key,
    t,
  ]);

  const selectedBoothGroup = useMemo(() => {
    if (!selectedPin) return null;
    return (
      boothPins.find((g) => g.companies.some((c) => c.id === selectedPin)) ?? null
    );
  }, [boothPins, selectedPin]);

  const searchFiltered = useMemo(
    () =>
      search
        ? companies.filter(
            (c) =>
              c.name.toLowerCase().includes(search.toLowerCase()) ||
              c.booth.toLowerCase().includes(search.toLowerCase()),
          )
        : [],
    [companies, search],
  );

  const selectFromSearch = (companyId: string) => {
    maxPlatform.hapticFeedback("light");
    setSelectedTechId(null);
    setSelectedPin(companyId);
    setSearch("");
  };

  const selectPin = (companyId: string | null) => {
    setSelectedTechId(null);
    setSelectedPin(companyId);
  };

  const selectTech = (markerId: string | null) => {
    setSelectedPin(null);
    setSelectedTechId(markerId);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full w-full overflow-hidden">
      <PageHeader
        className="shrink-0"
        title={t("map.title")}
        subtitle={
          loading
            ? t("map.subtitle_loading")
            : (() => {
                const s = t("map.subtitle", { n: companies.length });
                return s && s !== "map.subtitle" ? s : undefined;
              })()
        }
        back
        backTo="/home"
      />
      <div
        className="relative flex-1 min-h-0 w-full overflow-hidden bg-slate-200"
        style={{
          // Flex item must keep a definite height in Xiaomi/MAX WebView.
          flex: "1 1 0%",
          minHeight: 0,
        }}
      >
        {/* Working area: full screen under floating tab bar */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Company / booth search overlay */}
          {!loading && mapPublished && companies.length > 0 && (
            <div className="absolute top-2 left-2 right-2 z-30">
              <div className="relative">
                <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
                  <Search size={16} className="text-gray-400 flex-shrink-0" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("map.search_placeholder", {
                      defaultValue: "Поиск компании или стенда",
                    })}
                    className="flex-1 text-[14px] bg-transparent outline-none text-gray-800 placeholder-gray-400 min-w-0"
                  />
                  {search ? (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                      aria-label={t("map.close")}
                    >
                      <X size={14} className="text-gray-500" />
                    </button>
                  ) : null}
                </div>
                {searchFiltered.length > 0 && (
                  <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-xl shadow-lg border border-gray-100 py-1 max-h-52 overflow-y-auto">
                    {searchFiltered.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectFromSearch(c.id)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 active:bg-gray-50 text-left"
                      >
                        <MapPin size={14} className="text-indigo-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[13px] text-gray-900 truncate">{c.name}</div>
                          <div className="text-[11px] text-gray-400">
                            {formatCompanyLocation({
                              booth: c.booth,
                              hall: c.hall,
                              hideHall,
                            }) || c.booth || c.hall}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {(loading || (mapPublished && hallHasPlan(activeHall) && !floorReady)) && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-slate-100">
              <Loader2 size={32} className="animate-spin text-gray-400" />
              <p className="text-[13px] text-gray-500">{t("map.map_loading")}</p>
            </div>
          )}

          {loadError && (
            <div className="absolute inset-x-4 top-4 z-40 rounded-xl bg-red-50 border border-red-200 p-3 text-[12px] text-red-700">
              {t("map.load_error", { error: loadError })}
            </div>
          )}

          {!loading && (!mapPublished || !hallHasPlan(activeHall)) && !loadError && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-50 px-8">
              <div className="text-center max-w-xs space-y-3">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <MapPin size={28} className="text-slate-400" strokeWidth={1.5} />
                </div>
                <p className="text-[15px] text-gray-600 leading-snug font-medium">
                  {t(mapPublished ? "map.no_floorplan" : "map.not_ready")}
                </p>
              </div>
            </div>
          )}

          {mapPublished && hallHasPlan(activeHall) ? (
          <MapViewport
            ref={viewportRef}
            fill
            boardWidth={
              boardRotation === 90 || boardRotation === 270 ? board.height : board.width
            }
            boardHeight={
              boardRotation === 90 || boardRotation === 270 ? board.width : board.height
            }
            fitAlignY="center"
            /* Floating search (~48) + gap; tab bar sits outside the map flex area. */
            fitInsetTop={56}
            fitInsetBottom={8}
            fitKey={`${activeHall?.id ?? ""}:${board.width}x${board.height}:${schemeKey}:${boardRotation}`}
            onTransform={(t) => {
              setViewZoom((prev) => (Math.abs(prev - t.zoom) < 0.002 ? prev : t.zoom));
            }}
          >
          {/* Placement board — no Corel underlay in the visitor app. */}
          <div
            className="relative w-full h-full bg-white border border-gray-300 overflow-hidden"
            style={{
              width:
                boardRotation === 90 || boardRotation === 270 ? board.height : board.width,
              height:
                boardRotation === 90 || boardRotation === 270 ? board.width : board.height,
              visibility: floorReady ? "visible" : "hidden",
            }}
          >
            <div
              className="absolute origin-center"
              style={{
                width: board.width,
                height: board.height,
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) rotate(${boardRotation}deg)`,
              }}
            >
            {/* Plain placement board (no underlay, no decorative grid). */}
            <div className="absolute inset-0" style={{ backgroundColor: "#eef2f7" }} />

            {archMarkers.length > 0 ? (
              <FloorplanOverlayMarkers
                objects={archMarkers}
                selectedId={selectedTechId}
                interactive
                viewZoom={viewZoom}
                labelScale={labelScale}
                boardWidth={board.width}
                boardHeight={board.height}
                boardRotation={boardRotation}
                labelsUpright={labelsUpright}
                pinLabelRotationDeg={pinLabelRotationDeg}
                selectedColor={eventPrimary}
                accentById={markerAccentById}
                onSelect={(mid) => {
                  maxPlatform.hapticFeedback("light");
                  selectTech(mid === selectedTechId ? null : mid);
                }}
              />
            ) : null}

            {boothPins.map((group) => {
              const isSelected = group.companies.some((c) => c.id === selectedPin);
              const isHighlighted = group.companies.some((c) => c.id === highlightId);
              const active = isSelected || isHighlighted;
              const leadCompany =
                group.companies.find((c) => c.id === selectedPin || c.id === highlightId) ??
                group.companies[0];
              const pinColor = group.color;
              const activeColor = eventPrimary;
              const z = Math.max(0.05, viewZoom);
              const inv = 1 / z;
              const borderPx = Math.max(
                0.35,
                Number(((active ? 2.2 : 1) * inv).toFixed(3)),
              );
              const boothText =
                (group.booth || "").trim() || (leadCompany.booth || "").trim();
              const pinName = (leadCompany.mapLabel ?? "").trim() || leadCompany.name;
              const perRot = leadCompany.mapLabelRot ?? 0;
              const nameRot = normRot(
                (labelsUpright ? -boardRotation : 0) + pinLabelRotationDeg + perRot,
              );
              const perScale =
                typeof leadCompany.mapLabelScale === "number" && leadCompany.mapLabelScale > 0
                  ? Math.min(2.5, Math.max(0.5, leadCompany.mapLabelScale))
                  : 1;
              // Do not cap at 1 — admin per-pin / global sliders must enlarge text too.
              const scale = Math.min(2.5, Math.max(0.45, labelScale) * perScale);
              const boxW = Math.max(group.w, 1.2);
              const boxH = Math.max(group.h, 1.2);
              const multi = group.companies.length > 1;
              const nameLines = (
                multi
                  ? Math.min(4, group.companies.length)
                  : leadCompany.mapLabelLines === 1
                    ? 1
                    : leadCompany.mapLabelLines === 3
                      ? 3
                      : 2
              ) as 1 | 2 | 3 | 4;
              const names = multi
                ? group.companies.map((c) => (c.mapLabel ?? "").trim() || c.name)
                : pinName
                  ? [pinName]
                  : [];

              return (
                <button
                  key={group.key}
                  type="button"
                  data-map-pin
                  onPointerDown={(e) => {
                    pinTapRef.current = { x: e.clientX, y: e.clientY };
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const start = pinTapRef.current;
                    pinTapRef.current = null;
                    if (
                      start &&
                      Math.hypot(e.clientX - start.x, e.clientY - start.y) > 14
                    ) {
                      return;
                    }
                    maxPlatform.hapticFeedback("light");
                    selectPin(isSelected ? null : leadCompany.id);
                  }}
                  className="absolute touch-none overflow-hidden"
                  style={{
                    left: `${group.left}%`,
                    top: `${group.top}%`,
                    width: `${boxW}%`,
                    height: `${boxH}%`,
                    zIndex: active ? 40 : 20,
                    borderRadius: 6,
                    borderStyle: "solid",
                    borderWidth: borderPx,
                    borderColor: active ? activeColor : pinColor,
                    backgroundColor: active
                      ? eventHexAlpha(activeColor, "66")
                      : `${pinColor}40`,
                    boxShadow: active
                      ? `0 0 0 ${Math.max(1.5, 2 * inv).toFixed(2)}px ${eventHexRgba(activeColor, 0.45)}, 0 0 ${Math.max(6, 12 * inv).toFixed(1)}px ${eventHexRgba(activeColor, 0.4)}`
                      : "none",
                    outline: "none",
                    fontFamily: "system-ui, Segoe UI, sans-serif",
                  }}
                  aria-label={
                    multi
                      ? `${group.booth || leadCompany.name} (${group.companies.length})`
                      : leadCompany.name
                  }
                >
                  <PinCardLabels
                    boothCode={boothText}
                    names={names}
                    boxWPct={boxW}
                    boxHPct={boxH}
                    boardW={board.width}
                    boardH={board.height}
                    boardRotation={boardRotation}
                    nameRotation={nameRot}
                    nameLines={nameLines}
                    labelScale={scale}
                  />
                </button>
              );
            })}
            </div>
          </div>
        </MapViewport>
          ) : null}
        </div>
      </div>

      {/* Portal to body: Xiaomi WebView clips position:fixed inside overflow:hidden ancestors. */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {selectedCompany && (
              <motion.div
                key={selectedCompany.id}
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 24, opacity: 0 }}
                transition={{ type: "spring", damping: 26, stiffness: 320 }}
                className="pointer-events-auto fixed inset-x-0 z-[60] px-4"
                style={{
                  bottom: "calc(5.75rem + env(safe-area-inset-bottom, 0px))",
                }}
              >
                <div className="mx-auto max-w-lg rounded-2xl bg-white border border-gray-200 shadow-2xl p-4">
                  <div className="flex items-start gap-3">
                    <CompanyAvatar name={selectedCompany.name} logoUrl={selectedCompany.logoUrl} size="lg" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] text-gray-900 font-medium truncate">{selectedCompany.name}</p>
                      <p className="text-[12px] text-gray-500 mt-0.5">
                        {formatCategoriesDisplay(selectedCompany.category)}
                      </p>
                      {(selectedCompany.booth || (!hideHall && selectedCompany.hall)) && (
                        <p className="text-[12px] text-gray-600 mt-0.5 font-medium">
                          {formatCompanyLocation({
                            booth: selectedCompany.booth,
                            hall: selectedCompany.hall,
                            hideHall,
                          }) ||
                            formatBoothLabel(selectedCompany.booth) ||
                            selectedCompany.booth}
                        </p>
                      )}
                      {selectedBoothGroup && selectedBoothGroup.companies.length > 1 && (
                        <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                          <p className="text-[11px] text-gray-400">
                            {t("map.shared_booth", {
                              defaultValue: "На этом стенде",
                            })}
                          </p>
                          {selectedBoothGroup.companies.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => selectPin(c.id)}
                              className={`block w-full text-left text-[12px] truncate rounded-md px-2 py-1 ${
                                c.id === selectedCompany.id
                                  ? "bg-indigo-50 text-indigo-700 font-medium"
                                  : "text-gray-700 active:bg-gray-50"
                              }`}
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/companies/${selectedCompany.id}`)}
                        className="text-[13px] text-indigo-500 mt-2 font-medium"
                      >
                        {t("map.details")}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => selectPin(null)}
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                      aria-label={t("map.close")}
                    >
                      <X size={16} className="text-gray-500" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
            {selectedTechCard && selectedTech && (
              <motion.div
                key={`tech-${selectedTech.id}`}
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 24, opacity: 0 }}
                transition={{ type: "spring", damping: 26, stiffness: 320 }}
                className="pointer-events-auto fixed inset-x-0 z-[60] px-4"
                style={{
                  bottom: "calc(5.75rem + env(safe-area-inset-bottom, 0px))",
                }}
              >
                <div className="mx-auto max-w-lg rounded-2xl bg-white border border-gray-200 shadow-2xl p-4">
                  <div className="flex items-start gap-3">
                    {selectedTechCard.showLogo ? (
                      <CompanyAvatar
                        name={selectedTechCard.title}
                        logoUrl={eventLogoUrl}
                        size="lg"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <TechKindIcon kind={selectedTechCard.kind} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] text-gray-900 font-medium truncate">
                        {selectedTechCard.title}
                      </p>
                      <p className="text-[12px] text-gray-500 mt-0.5">
                        {selectedTechCard.subtitle}
                      </p>
                      {selectedTech.kind === "event_link" && selectedTech.linkEventId ? (
                        <button
                          type="button"
                          onClick={() => {
                            const targetId = selectedTech.linkEventId!;
                            maxPlatform.hapticFeedback("medium");
                            selectTech(null);
                            selectEvent(targetId);
                            navigate("/map");
                            toast.message("Открываем карту соседней выставки");
                          }}
                          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl text-white text-[13px] font-semibold py-2.5"
                          style={{ backgroundColor: selectedLinkAccent }}
                        >
                          <ArrowRight size={16} />
                          Перейти на карту
                        </button>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => selectTech(null)}
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                      aria-label={t("map.close")}
                    >
                      <X size={16} className="text-gray-500" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}
